import React from "react";

import { strings } from "@perceptron/constants";
import { formatString } from "@perceptron/utils/string";

import type { ToolCallEntry, ToolCallLinkEntry } from "@demo/store/tool-calls";

const copy = strings.toolCall;

/** Running / done copy per family, adapted to the payload rather than the name. */
/** Tools whose pill copy differs from their family's. Keyed by the full dotted name. */
const TOOL_COPY: Record<string, { running: string; done: string }> = {
  "search.text_image_search": { running: copy.running.textImageSearch, done: copy.done.textImageSearch },
  "search.image_search": { running: copy.running.imageSearch, done: copy.done.imageSearch },
};

/**
 * The error line beside a failed call's pill, per the failed designs: a fixed line when the search
 * backend could not be reached, the producer's message when the model sent a bad request, and a
 * bare "Error" for anything else.
 */
export function describeToolCallError(call: ToolCallEntry): string {
  switch (call.error?.code) {
    case "SEARCH_UNAVAILABLE":
      return copy.errors.searchUnavailable;
    case "TOOL_BAD_ARGUMENTS":
      return call.error.message ? formatString(copy.errors.withMessage, call.error.message) : copy.errors.generic;
    default:
      return copy.errors.generic;
  }
}

/**
 * The pill's label. `isActive` is the card's running look, which outlives the wire's `running`
 * status until the model's next text arrives (see `ToolCallEntryCard`).
 */
export function describeToolCallStatus(call: ToolCallEntry, isActive: boolean = call.status === "running"): string {
  if (call.status === "cancelled") return copy.cancelled;

  // A failed call keeps its tool's name in the pill (in the error tone); the error is the summary.
  const toolCopy = TOOL_COPY[call.toolName];
  if (toolCopy) return isActive ? toolCopy.running : toolCopy.done;

  switch (call.family) {
    case "search":
      // The result count lives in the expanded body, so the completed pill is just the tool's name.
      return isActive ? copy.running.search : copy.done.search;
    case "visit":
      return isActive ? copy.running.visit : copy.done.visit;
    // The zoom renders as a chip without a status pill; only the plain row would ever ask.
    case "zoom":
    case "generic":
      return isActive
        ? formatString(copy.running.generic, call.toolName)
        : formatString(copy.done.generic, call.toolName);
  }
}

/** The context line under the status: what was searched or zoomed into, or which pages were read toward what. */
export function describeToolCallInput(call: ToolCallEntry): string | null {
  const input = call.input;
  if (!input) return null;
  switch (input.kind) {
    case "image": {
      const { asset_idx, region } = input.image;
      return region ? formatString(copy.input.imageRegion, asset_idx) : formatString(copy.input.image, asset_idx);
    }
    case "visit":
      return formatString(copy.input.goal, input.goal);
    case "text":
    default:
      return formatString(copy.input.query, input.query);
  }
}

const linkLabel = (entry: ToolCallLinkEntry): string => {
  const { link } = entry;
  // Fallback: title → domain → url; an image-only link falls back to "(untitled)".
  return link.title ?? link.domain ?? link.url ?? copy.untitled;
};

const ToolCallLinkItem: React.FC<{ entry: ToolCallLinkEntry }> = ({ entry }) => {
  const { link } = entry;
  const label = linkLabel(entry);
  const status = link.status ?? "ok";
  return (
    <li className="flex flex-col gap-1" data-index={entry.index} data-testid="tool-call-link">
      <div className="flex items-center gap-2">
        {link.image && (
          // Thumbnails may be `data:` URLs the harness re-encoded; next/image cannot optimize those.
          // eslint-disable-next-line @next/next/no-img-element
          <img
            alt={label}
            className="h-8 w-8 shrink-0 object-cover"
            height={link.image.height}
            src={link.image.url}
            width={link.image.width}
          />
        )}
        {link.url ? (
          <a className="underline" href={link.url} rel="noreferrer noopener" target="_blank" title={link.url}>
            {label}
          </a>
        ) : (
          <span>{label}</span>
        )}
        {link.domain && link.title && <span className="text-foreground-secondary">{link.domain}</span>}
        {status !== "ok" && <span className="text-foreground-secondary">{copy.linkStatus[status]}</span>}
      </div>
      {link.summary && (
        // Plain escaped text with line breaks preserved: page content never reaches the markdown
        // or annotation pipeline, so a page cannot inject boxes into the run.
        <p className="whitespace-pre-wrap text-foreground-secondary">{link.summary}</p>
      )}
    </li>
  );
};

export const ToolCallRow: React.FC<{ call: ToolCallEntry }> = ({ call }) => {
  const inputLine = describeToolCallInput(call);
  return (
    <div
      className="flex flex-col gap-1 border-l-2 border-foreground-secondary pl-3"
      data-status={call.status}
      data-testid="tool-call-row"
      data-tool-call-id={call.id}
    >
      <div className="flex items-center gap-2">
        <span className="font-mono">{call.toolName}</span>
        <span>{describeToolCallStatus(call)}</span>
      </div>
      {inputLine && <div className="text-foreground-secondary">{inputLine}</div>}
      {call.error && <div className="text-foreground-secondary">{call.error.message}</div>}
      {call.links.length > 0 && (
        <ul className="flex flex-col gap-1">
          {call.links.map((entry) => (
            <ToolCallLinkItem entry={entry} key={entry.index} />
          ))}
        </ul>
      )}
    </div>
  );
};
